import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer, DownloadSimple } from "@phosphor-icons/react";
import api, { formatINR, API_BASE } from "@/lib/api";
import { Spinner } from "@/components/Loaders";
import { Button } from "@/components/ui/button";
import { fmtStatus } from "@/constants/status";

const downloadPdf = (id, ref) => {
  const token = localStorage.getItem("avb_token");
  fetch(`${API_BASE}/bookings/${id}/invoice/pdf`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.blob()).then((b) => {
      const url = URL.createObjectURL(b);
      const a = document.createElement("a"); a.href = url; a.download = `receipt_${ref || id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    });
};

export default function Invoice() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);

  useEffect(() => {
    api.get(`/bookings/${id}/invoice`).then((r) => setInv(r.data)).catch(() => setInv(false));
  }, [id]);

  if (inv === null) return <div className="flex h-screen items-center justify-center"><Spinner /></div>;
  if (inv === false) return <div className="flex h-screen items-center justify-center text-stone-500">Receipt not found.</div>;

  const Row = ({ label, value, bold }) => (
    <div className={`flex justify-between py-1.5 text-sm ${bold ? "font-semibold" : ""}`}>
      <span className="text-stone-500">{label}</span>
      <span className="tabular-nums text-stone-800">{value}</span>
    </div>
  );

  const cp = inv.cancellation_policy;

  return (
    <div className="min-h-screen bg-stone-100 py-6 print:bg-white">
      <div className="mx-auto max-w-2xl px-4">
        <div className="mb-4 flex items-center justify-between print:hidden">
          <button data-testid="inv-back" onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-stone-600"><ArrowLeft size={18} /> Back</button>
          <div className="flex gap-2">
            <Button data-testid="inv-download" variant="outline" onClick={() => downloadPdf(id, inv.booking_ref)} className="gap-2 rounded-full"><DownloadSimple size={16} /> Download PDF</Button>
            <Button data-testid="inv-print" onClick={() => window.print()} className="gap-2 rounded-full bg-primary hover:bg-green-900"><Printer size={16} /> Print</Button>
          </div>
        </div>

        <div className="rounded-2xl border border-stone-200 bg-white p-8 shadow-sm">
          <div className="flex items-start justify-between border-b border-stone-200 pb-5">
            <div>
              <p className="font-heading text-2xl font-extrabold tracking-tight text-primary">Avinya <span className="font-semibold text-stone-800">Bookings</span></p>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.28em] text-amber-600">Book · Relax · Celebrate</p>
            </div>
            <div className="text-right">
              <p className="font-heading text-lg font-bold text-stone-900">BOOKING RECEIPT</p>
              <p data-testid="inv-number" className="font-mono text-sm text-stone-600">{inv.invoice_number}</p>
              <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${inv.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{inv.payment_status === "paid" ? "Advance Paid" : fmtStatus(inv.payment_status)}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 border-b border-stone-200 py-5 text-sm">
            <div>
              <p className="mb-1 font-semibold text-stone-800">Billed To</p>
              <p className="text-stone-600">{inv.customer_name}</p>
              <p className="text-stone-600">{inv.customer_phone}</p>
            </div>
            <div className="text-right">
              <p className="mb-1 font-semibold text-stone-800">Booking</p>
              <p className="font-mono text-stone-600">{inv.booking_ref}</p>
              <p className="text-stone-600">Date: {inv.date}</p>
              {inv.payment_id && <p className="text-stone-600">Payment: {inv.payment_id}</p>}
            </div>
          </div>

          <div className="border-b border-stone-200 py-5">
            <div className="mb-2 flex justify-between text-xs font-semibold uppercase text-stone-400">
              <span>Description</span><span>Amount</span>
            </div>
            <div className="flex justify-between py-1.5 text-sm">
              <span className="text-stone-800">{inv.property_name} — {inv.booking_type} ({inv.guests} guests, {inv.package})<br /><span className="text-xs text-stone-500">{inv.category} · {inv.property_location}</span></span>
              <span className="tabular-nums text-stone-800">{formatINR(inv.base_amount)}</span>
            </div>
          </div>

          <div className="ml-auto mt-4 max-w-xs">
            <Row label="Base amount" value={formatINR(inv.base_amount)} />
            {inv.service_fee > 0 && <Row label="Service fee" value={formatINR(inv.service_fee)} />}
            <Row label={`GST (${inv.gst_rate}%)`} value={formatINR(inv.gst_amount)} />
            <div className="mt-2 border-t border-stone-200 pt-2">
              <Row label="Total booking value" value={formatINR(inv.total_amount)} bold />
            </div>
            <div className="mt-2 rounded-xl bg-accent/50 p-3">
              <Row label="Advance paid" value={formatINR(inv.advance_amount)} bold />
              <Row label="Balance (at venue)" value={formatINR(inv.balance_amount)} />
            </div>
          </div>

          {cp && (
            <div className="mt-6 rounded-xl bg-stone-50 p-4 text-xs text-stone-500">
              <p className="mb-1 font-semibold text-stone-600">Cancellation Policy</p>
              <p>{cp.full_refund_days}+ days before booking: 100% refund of advance</p>
              <p>{cp.partial_refund_days}–{cp.full_refund_days - 1} days: {cp.partial_refund_pct}% refund</p>
              <p>Within {cp.no_refund_hours} hours of booking: no refund</p>
            </div>
          )}

          <p className="mt-8 text-center text-xs text-stone-400">
            This is a computer-generated receipt from Avinya Bookings. {inv.gstin ? `GSTIN: ${inv.gstin}` : "GSTIN: not registered"}.
          </p>
        </div>
      </div>
    </div>
  );
}
